import { UISref, useRouter } from "@uirouter/react";
import { useAtom, useAtomValue } from "jotai";
import moment from "moment";
import { FunctionComponent, useMemo } from "react";
import { InfoButton, PrimaryButton } from "../../../components/button";
import { Icon } from "../../../components/icons";
import { KycIcon } from "../../../components/icons/status";
import { Resource } from "../../../components/loader/resource";
import { ProcessingStatus } from "../../../components/processing-status";
import { Table } from "../../../components/table";
import { TableHeaders } from "../../../components/table/Head";
import { Widget } from "../../../components/widget";
import { validationFields } from "../../../store/common/models/fields";
import { useAllParticipations } from "../../../store/participations/queries";
import { ParticipationsDTO } from "../../../store/participations/models/dto";
import { colours } from "../../../styles/colours";
import { FraudRoutes } from "../../kyc-kyb-aml/routes";
import { ParticipationsRoutes as routes } from "../routes";
import { WidgetAdornment } from "./adornment";
import { participationPagination } from "./atoms";
import { NoParticipations } from "./base";

type SingleParticipation = ParticipationsDTO.Responses.GetSingle.T;
type fieldNames = keyof SingleParticipation;

function useHeaders(): TableHeaders<fieldNames> {
  const email = validationFields.email.useName();
  const referrer = validationFields.referrer.useName();
  const kycStatus = validationFields.kycStatus.useName();
  const processingStatus = validationFields.processingStatus.useName();

  return useMemo(
    () => [
      {
        name: "editable",
        title: "Name",
      },
      {
        name: "user",
        title: email,
      },
      {
        name: "createdDate",
        title: "Submission Date",
      },
      {
        name: "referrer",
        title: referrer,
      },
      {
        name: "kycStatus",
        title: kycStatus,
      },
      {
        name: "processingStatus",
        title: processingStatus,
        hiddenMobile: true,
      },
      {
        name: "fraudCheckId",
        title: "View KYC",
        hiddenMobile: true,
      },
      {
        name: "id",
        title: "",
        centered: true,
        hiddenMobile: true,
      },
    ],
    [email, referrer, kycStatus, processingStatus]
  );
}

type ParticipationListProps = {
  hideEmail?: boolean;
  hideName?: boolean;
  hideViewKyc?: boolean;
};

export const ParticipationList: FunctionComponent<ParticipationListProps> = (
  props
) => (
  <Resource r={useAllParticipations(useAtomValue(participationPagination))}>
    {(data) => (
      <Widget adornment={<WidgetAdornment />}>
        <PresentationalParticipationList participationList={data} {...props} />
      </Widget>
    )}
  </Resource>
);

export const PresentationalParticipationList: FunctionComponent<
  {
    participationList: ParticipationsDTO.Responses.GetAll.T;
  } & ParticipationListProps
> = ({ participationList, hideEmail, hideName, hideViewKyc }) => {
  const router = useRouter();
  const headers = useHeaders();
  const [pagination, setPagination] = useAtom(participationPagination);

  const filteredHeaders = useMemo(() => {
    let result = headers;
    if (hideEmail) result = result.filter((x) => x.name !== "user");
    if (hideName) result = result.filter((x) => x.name !== "editable");
    if (hideViewKyc) result = result.filter((x) => x.name !== "fraudCheckId");
    return result;
  }, [hideEmail, headers, hideName, hideViewKyc]);

  return useMemo(
    () => (
      <>
        {participationList.pageable.totalElements < 1 ? (
          <NoParticipations />
        ) : (
          <>
            <Table
              data={participationList.content}
              head={filteredHeaders}
              renderFields={{
                id: ({ id }) => (
                  <UISref to={routes.names.single} params={{ id }}>
                    <a>
                      <PrimaryButton variant="contained">
                        <Icon
                          type="export"
                          rotateAmount={270}
                          colour1={colours.white}
                        />
                      </PrimaryButton>
                    </a>
                  </UISref>
                ),
                user: ({ user }) => <>{user.emailAddress}</>,
                editable: ({ user }) => (
                  <>
                    {user.firstName} {user.lastName}
                  </>
                ),
                createdDate: ({ createdDate }) => (
                  <>
                    {moment.utc(createdDate).local().format("yyy-MM-DD HH:mm")}
                  </>
                ),
                kycStatus: ({ kycStatus }) => KycIcon(kycStatus, "kyc"),
                processingStatus: ({ processingStatus }) => (
                  <ProcessingStatus processingStatus={processingStatus} />
                ),
                fraudCheckId: ({ fraudCheckId }) =>
                  fraudCheckId ? (
                    <InfoButton
                      onClick={(e) => {
                        e.stopPropagation();
                        router.stateService.go(
                          FraudRoutes.names.singleIndividualKyc,
                          { fraudCheckId }
                        );
                      }}
                    >
                      KYC
                    </InfoButton>
                  ) : null,
              }}
              unsortable={["editable"]}
              pagination={participationList.pageable}
              onPaginationChange={setPagination}
              pageableState={pagination}
              rowOnClick={({ id }) => {
                router.stateService.go(routes.names.single, { id });
              }}
            />
          </>
        )}
      </>
    ),
    [participationList, router, filteredHeaders, pagination]
  );
};
